import { useState, useEffect, useRef } from "react";
import { MainLayout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  FileText, Plus, Search, Send, Download, PenTool, Wrench,
  Receipt, Trash2, Edit, ChevronRight, Loader2, CheckCircle2,
  X, Save, Eye, RefreshCw, Clock, Check, XCircle, Link, Copy, ShoppingCart
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ComboInput, ProductLine, StockSiteCard, useCatalogMeta, modelesDe, lineTotal, lineDiscount } from "../components/QuoteCatalog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  brouillon: { label: "Brouillon",  color: "bg-slate-500/20 text-slate-400",   icon: Clock },
  envoye:    { label: "Envoyé",     color: "bg-blue-500/20 text-blue-400",     icon: Send },
  accepte:   { label: "Accepté",    color: "bg-emerald-500/20 text-emerald-400", icon: Check },
  refuse:    { label: "Refusé",     color: "bg-red-500/20 text-red-400",       icon: XCircle },
  facture:   { label: "Facturé",    color: "bg-purple-500/20 text-purple-400", icon: Receipt },
};

// Objet du devis déduit de ce qui est déjà saisi (l'objet reste modifiable)
const autoSubject = (f) => {
  const appareil = [f.device_brand, f.device_model].filter(Boolean).join(" ").trim();
  if (f.quote_type === "reparation") {
    const panne = (f.problem_description || "").trim();
    return ["Réparation", panne ? panne.charAt(0).toLowerCase() + panne.slice(1) : "", appareil ? `— ${appareil}` : ""].filter(Boolean).join(" ");
  }
  const produits = (f.items || []).map(i => (i.name || "").trim()).filter(Boolean);
  if (produits.length === 1) return produits[0];
  return appareil ? `Devis produits — ${appareil}` : "Devis produits";
};

const EMPTY_QUOTE = {
  client_name: "", client_email: "", client_phone: "",
  subject: "", notes: "", validity_date: "", tva_rate: 0,
  quote_type: "reparation",          // "reparation" | "produits"
  device_brand: "", device_model: "", problem_description: "",
  items: [{ name: "", quantity: 1, unit_price: 0 }],
};

export default function QuotesPage() {
  const [quotes, setQuotes]           = useState([]);
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState("");
  const [createOpen, setCreateOpen]   = useState(false);
  const [detailOpen, setDetailOpen]   = useState(false);
  const [signOpen, setSignOpen]       = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [form, setForm]               = useState(EMPTY_QUOTE);
  const [saving, setSaving]           = useState(false);
  const [sending, setSending]         = useState(false);
  const [converting, setConverting]   = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing]     = useState(false);
  const [hasSig, setHasSig]           = useState(false);

  const token = () => localStorage.getItem("token");
  const headers = () => ({ Authorization: `Bearer ${token()}` });

  useEffect(() => { fetchQuotes(); }, []);
  const meta = useCatalogMeta(createOpen);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/quotes`, { headers: headers() });
      setQuotes(res.data);
    } catch { toast.error("Erreur chargement devis"); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (form.quote_type === "reparation" && !form.device_brand.trim()) {
      toast.error("Indiquez la marque de l'appareil"); return;
    }
    if (form.quote_type === "reparation" && !form.device_model.trim()) {
      toast.error("Indiquez le modèle de l'appareil"); return;
    }
    const items = form.items.filter(i => (i.name || "").trim());
    if (items.length === 0) { toast.error("Ajoutez au moins un article"); return; }
    const payload = { ...form, items, subject: (form.subject || "").trim() || autoSubject({ ...form, items }) };
    setSaving(true);
    try {
      if (selectedQuote?.id) {
        await axios.put(`${API_URL}/api/quotes/${selectedQuote.id}`, payload, { headers: headers() });
        toast.success("Devis mis à jour");
      } else {
        await axios.post(`${API_URL}/api/quotes`, payload, { headers: headers() });
        toast.success("Devis créé");
      }
      setCreateOpen(false);
      setSelectedQuote(null);
      setForm(EMPTY_QUOTE);
      fetchQuotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur sauvegarde");
    } finally { setSaving(false); }
  };

  const handleSendEmail = async (quote) => {
    if (!quote.client_email) { toast.error("Email client manquant"); return; }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/quotes/${quote.id}/send-email`, {}, { headers: headers() });
      toast.success("Devis envoyé par email ✅");
      fetchQuotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur envoi email");
    } finally { setSending(false); }
  };

  const handleDownloadPDF = async (quote) => {
    try {
      const res = await axios.get(`${API_URL}/api/quotes/${quote.id}/pdf`, {
        headers: headers(), responseType: "blob"
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `devis_${quote.quote_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erreur téléchargement PDF"); }
  };

  const handleConvertRepair = async (quote) => {
    setConverting(true);
    try {
      const res = await axios.post(`${API_URL}/api/quotes/${quote.id}/convert-repair`, {}, { headers: headers() });
      toast.success("Réparation créée depuis le devis ✅");
      setDetailOpen(false);
      fetchQuotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur conversion");
    } finally { setConverting(false); }
  };

  const handleConvertOrder = async (quote) => {
    if (quote.status !== "accepte") { toast.error("Le devis doit être signé pour créer une commande"); return; }
    setConverting(true);
    try {
      const res = await axios.post(`${API_URL}/api/quotes/${quote.id}/convert-order`, {}, { headers: headers() });
      toast.success(`Commande ${res.data.order_number} créée !`);
      fetchQuotes();
      setSelectedQuote(null);
      setDetailOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur création commande");
    } finally { setConverting(false); }
  };

  const handleConvertInvoice = async (quote) => {
    setConverting(true);
    try {
      await axios.post(`${API_URL}/api/quotes/${quote.id}/convert-invoice`, {}, { headers: headers() });
      toast.success("Facture créée depuis le devis ✅");
      setDetailOpen(false);
      fetchQuotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur conversion");
    } finally { setConverting(false); }
  };

  const handleDelete = async (quote) => {
    if (!window.confirm(`Supprimer le devis ${quote.quote_number} ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/quotes/${quote.id}`, { headers: headers() });
      toast.success("Devis supprimé");
      setDetailOpen(false);
      fetchQuotes();
    } catch { toast.error("Erreur suppression"); }
  };

  // Signature canvas
  const startDraw = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX || e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY || e.clientY) - rect.top;
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX || e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY || e.clientY) - rect.top;
    ctx.lineTo(x, y); ctx.stroke();
    setHasSig(true);
  };
  const stopDraw = () => setIsDrawing(false);
  const clearSig = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasSig(false);
  };
  const saveSig = async () => {
    if (!selectedQuote?.client_name) { toast.error("Le nom du client est requis"); return; }
    if (!hasSig) { toast.error("Le client doit signer"); return; }
    const sig = canvasRef.current.toDataURL("image/png");
    try {
      // Sauvegarder les infos client + signature
      await axios.put(`${API_URL}/api/quotes/${selectedQuote.id}`, {
        client_name:  selectedQuote.client_name,
        client_email: selectedQuote.client_email,
        client_phone: selectedQuote.client_phone,
      }, { headers: headers() });
      await axios.post(`${API_URL}/api/quotes/${selectedQuote.id}/sign`, { signature: sig }, { headers: headers() });
      toast.success("Devis signé et accepté ✅");
      setSignOpen(false);
      fetchQuotes();
      if ((selectedQuote.items || []).some(i => i.store_product_id)) {
        const r = await axios.get(`${API_URL}/api/quotes/${selectedQuote.id}`, { headers: headers() });
        setSelectedQuote(r.data);
        setDetailOpen(true);
      }
    } catch { toast.error("Erreur lors de la signature"); }
  };

  const addItem  = () => setForm(f => ({ ...f, items: [...f.items, { name: "", quantity: 1, unit_price: "" }] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const patchItem = (i, patch) => setForm(f => ({
    ...f, items: f.items.map((item, idx) => idx === i ? { ...item, ...patch } : item)
  }));
  const reloadSelected = async () => {
    fetchQuotes();
    try {
      const r = await axios.get(`${API_URL}/api/quotes/${selectedQuote.id}`, { headers: headers() });
      setSelectedQuote(r.data);
    } catch { /* la liste est rechargée de toute façon */ }
  };

  // Totaux remises comprises (remise par unité sur chaque ligne)
  const calcTotal = (items, tva) => {
    const sub = items.reduce((s, i) => s + lineTotal(i), 0);
    const remises = items.reduce((s, i) => s + lineDiscount(i) * (parseFloat(i.quantity)||1), 0);
    const tvaAmt = sub * (parseFloat(tva)||0) / 100;
    return { subtotal: sub, remises, tvaAmt, total: sub + tvaAmt };
  };

  const filtered = quotes.filter(q =>
    !search || [q.quote_number, q.client_name, q.client_email, q.subject]
      .some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <MainLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-chivo text-2xl font-bold text-foreground flex items-center gap-2">
              <FileText className="w-6 h-6 text-primary" />
              Devis
            </h1>
            <p className="text-muted-foreground text-sm mt-0.5">{quotes.length} devis au total</p>
          </div>
          <Button onClick={() => { setForm(EMPTY_QUOTE); setSelectedQuote(null); setCreateOpen(true); }} className="bg-primary">
            <Plus className="w-4 h-4 mr-2" /> Nouveau devis
          </Button>
        </div>

        {/* Recherche */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un devis..." className="pl-9 bg-secondary/50" />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
            const count = quotes.filter(q => q.status === key).length;
            return (
              <Card key={key} className="bg-card border-border">
                <CardContent className="p-3 flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>
                  <span className="text-lg font-bold text-foreground ml-auto">{count}</span>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Liste */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aucun devis</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(q => {
              const cfg = STATUS_CONFIG[q.status] || STATUS_CONFIG.brouillon;
              const { total } = calcTotal(q.items || [], q.tva_rate || 0);
              return (
                <Card key={q.id} className="bg-card border-border hover:border-primary/30 cursor-pointer transition-all"
                  onClick={() => { setSelectedQuote(q); setDetailOpen(true); }}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                          <FileText className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm text-foreground">{q.quote_number}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>
                          </div>
                          <p className="text-sm text-foreground mt-0.5">{q.client_name}</p>
                          <p className="text-xs text-muted-foreground">{q.subject || "Sans objet"}</p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-bold text-primary">{total.toFixed(2)}€</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {new Date(q.created_at).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Dialog Créer/Éditer */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedQuote ? "Modifier le devis" : "Nouveau devis"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Type de devis — détermine ce qui est créé une fois le devis payé */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Type de devis *</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "reparation", t: "🔧 Réparation", d: "Crée une réparation au paiement" },
                  { v: "produits",   t: "📦 Produits",   d: "Crée une commande boutique" },
                ].map(o => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, quote_type: o.v }))}
                    className={`text-left p-3 rounded-lg border-2 transition ${
                      form.quote_type === o.v
                        ? "border-blue-500 bg-blue-500/15 text-foreground"
                        : "border-border hover:border-muted-foreground/40 text-foreground"
                    }`}
                  >
                    <div className="font-semibold text-sm">{o.t}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{o.d}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Appareil : marque + modèle (listes du site, filtrées en tapant) — servent aussi à proposer les produits */}
            <div className="p-3 rounded-lg bg-secondary/20 border border-border/50 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground">
                {form.quote_type === "reparation" ? "Appareil à réparer" : "Marque / modèle (facultatif — filtre les produits proposés)"}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Marque{form.quote_type === "reparation" && " *"}</label>
                  <ComboInput value={form.device_brand}
                    onChange={v => setForm(f => ({ ...f, device_brand: v, device_model: v === f.device_brand ? f.device_model : "" }))}
                    options={meta.brands} placeholder="Tapez : app, sams…" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Modèle{form.quote_type === "reparation" && " *"}</label>
                  <ComboInput value={form.device_model}
                    onChange={v => setForm(f => ({ ...f, device_model: v }))}
                    options={modelesDe(meta, form.device_brand)}
                    placeholder={form.device_brand ? "Tapez : 14 pro…" : "Choisissez d'abord la marque"}
                    emptyHint={form.device_brand ? "Modèle absent de la liste : la saisie libre est acceptée" : undefined} />
                </div>
              </div>
              {meta.erreur && <p className="text-[11px] text-amber-400">Catalogue du site injoignable : saisie libre.</p>}
              {form.quote_type === "reparation" && (
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Panne constatée</label>
                  <Input value={form.problem_description}
                         onChange={e => setForm(f => ({ ...f, problem_description: e.target.value }))}
                         placeholder="Ex : écran cassé, tactile HS" />
                </div>
              )}
            </div>
            {/* Infos client optionnelles */}
            <div className="p-3 rounded-xl bg-secondary/20 border border-border/50">
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                <span className="w-4 h-4 rounded-full bg-secondary flex items-center justify-center text-xs">i</span>
                Informations client — optionnelles (peuvent être renseignées à la signature)
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input value={form.client_name} onChange={e => setForm(f => ({...f, client_name: e.target.value}))} placeholder="Nom complet" />
                <Input value={form.client_email} onChange={e => setForm(f => ({...f, client_email: e.target.value}))} placeholder="email@..." type="email" />
                <Input value={form.client_phone} onChange={e => setForm(f => ({...f, client_phone: e.target.value}))} placeholder="0696..." />
              </div>
            </div>

            {/* Articles */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-foreground">Articles</label>
                <Button variant="outline" size="sm" onClick={addItem} className="text-xs">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground mb-2">Tapez un produit : ceux du site s'affichent avec leur prix et leur promo (reprise en remise). Pour un produit à déclinaisons, choisissez ensuite stockage, couleur… S'il n'existe pas, vous pouvez l'ajouter au site.</p>
              <div className="flex gap-2 items-center pb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                <span className="flex-1">Produit / prestation</span>
                <span className="w-14 text-center">Qté</span>
                <span className="w-24 text-center">Prix unit. €</span>
                <span className="w-20 text-center">Remise €/u</span>
                <span className="w-20 text-right">Total</span>
                <span className="w-6" />
              </div>
              <div className="space-y-2">
                {form.items.map((item, i) => (
                  <ProductLine key={i} item={item}
                    onChange={patch => patchItem(i, patch)}
                    onRemove={() => removeItem(i)}
                    brand={form.device_brand} model={form.device_model}
                    quoteType={form.quote_type} quoteNumber={selectedQuote?.quote_number} />
                ))}
              </div>
            </div>

            {/* Totaux */}
            {(() => {
              const { subtotal, remises, tvaAmt, total } = calcTotal(form.items, form.tva_rate);
              return (
                <div className="p-3 rounded-xl bg-secondary/30 space-y-1 text-sm">
                  {remises > 0 && <div className="flex justify-between text-red-400"><span>Remises accordées</span><span>−{remises.toFixed(2)}€</span></div>}
                  <div className="flex justify-between"><span className="text-muted-foreground">Sous-total HT</span><span>{subtotal.toFixed(2)}€</span></div>
                  {parseFloat(form.tva_rate) > 0 && <div className="flex justify-between"><span className="text-muted-foreground">TVA ({form.tva_rate}%)</span><span>{tvaAmt.toFixed(2)}€</span></div>}
                  <div className="flex justify-between font-bold border-t border-border pt-1"><span>Total TTC</span><span className="text-primary">{total.toFixed(2)}€</span></div>
                </div>
              );
            })()}

            {/* Objet : déduit automatiquement, modifiable */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Objet du devis <span className="opacity-70">(facultatif — généré si vide)</span></label>
              <Input value={form.subject} onChange={e => setForm(f => ({...f, subject: e.target.value}))} placeholder={autoSubject(form) || "Objet du devis"} />
            </div>

            {/* Options */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">TVA (%)</label>
                <Input value={form.tva_rate} onChange={e => setForm(f => ({...f, tva_rate: e.target.value}))} type="number" placeholder="0" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Validité jusqu'au</label>
                <Input value={form.validity_date} onChange={e => setForm(f => ({...f, validity_date: e.target.value}))} type="date" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-muted-foreground mb-1 block">Notes</label>
                <Input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Conditions particulières..." />
              </div>
            </div>

            <Button className="w-full bg-primary" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {selectedQuote ? "Mettre à jour" : "Créer le devis"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Détail */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              {selectedQuote?.quote_number}
            </DialogTitle>
          </DialogHeader>
          {selectedQuote && (() => {
            const cfg = STATUS_CONFIG[selectedQuote.status] || STATUS_CONFIG.brouillon;
            const { remises, total } = calcTotal(selectedQuote.items||[], selectedQuote.tva_rate||0);
            return (
              <div className="space-y-4">
                {/* Statut */}
                <div className="flex items-center gap-2">
                  <span className={`text-sm px-3 py-1 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>
                  {selectedQuote.signed_at && <span className="text-xs text-emerald-400">✅ Signé le {new Date(selectedQuote.signed_at).toLocaleDateString("fr-FR")}</span>}
                </div>

                {/* Client */}
                <div className="p-3 rounded-xl bg-secondary/30">
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Client</p>
                  <p className="text-sm font-medium">{selectedQuote.client_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedQuote.client_email}</p>
                  <p className="text-sm text-muted-foreground">{selectedQuote.client_phone}</p>
                </div>

                {/* Articles */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Articles</p>
                  <div className="space-y-1">
                    {(selectedQuote.items||[]).map((item, i) => (
                      <div key={i} className="flex justify-between text-sm py-1 border-b border-border last:border-0">
                        <span>{item.name} <span className="text-muted-foreground">x{item.quantity}</span>
                          {item.store_product_id && <span className="ml-1 text-[10px] text-emerald-400" title="Produit du site">🔗</span>}
                          {item.stock_site_ajoute > 0 && <span className="ml-1 text-[10px] text-amber-400">+{item.stock_site_ajoute} en stock site</span>}
                          {lineDiscount(item) > 0 && (
                            <span className="block text-[11px] text-red-400">
                              {item.discount_label || "Remise"} : −{(lineDiscount(item) * (parseFloat(item.quantity)||1)).toFixed(2)}€
                            </span>
                          )}
                        </span>
                        <span className="text-right shrink-0">
                          {lineDiscount(item) > 0 && <span className="block text-[11px] text-muted-foreground line-through">{(parseFloat(item.unit_price||0)*parseFloat(item.quantity||1)).toFixed(2)}€</span>}
                          <span className="text-primary font-medium">{lineTotal(item).toFixed(2)}€</span>
                        </span>
                      </div>
                    ))}
                  </div>
                  {remises > 0 && (
                    <div className="flex justify-between text-sm text-red-400 mt-2">
                      <span>Remises accordées</span><span>−{remises.toFixed(2)}€</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold mt-2 pt-2 border-t border-border">
                    <span>Total TTC</span>
                    <span className="text-primary">{total.toFixed(2)}€</span>
                  </div>
                </div>

                <StockSiteCard key={selectedQuote.id + (selectedQuote.status || "")} quote={selectedQuote} onDone={reloadSelected} />

                {/* Actions */}
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleDownloadPDF(selectedQuote)} className="text-xs">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleSendEmail(selectedQuote)} disabled={sending} className="text-xs border-blue-500/40 text-blue-400">
                    {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                    Email
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    className="col-span-2 text-xs border-violet-500/40 text-violet-400 hover:bg-violet-500/10"
                    onClick={() => {
                      const link = `${window.location.origin}/devis/${selectedQuote.public_token}`;
                      navigator.clipboard.writeText(link);
                      toast.success("Lien copié ! Partagez-le avec votre client.");
                    }}
                  >
                    <Link className="w-3.5 h-3.5 mr-1.5" /> Copier le lien client
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setSignOpen(true); setHasSig(false); }} className="text-xs border-amber-500/40 text-amber-400">
                    <PenTool className="w-3.5 h-3.5 mr-1.5" /> Signature
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setForm({ ...EMPTY_QUOTE, ...selectedQuote }); setCreateOpen(true); }} className="text-xs">
                    <Edit className="w-3.5 h-3.5 mr-1.5" /> Modifier
                  </Button>
                  <Button size="sm" onClick={() => handleConvertOrder(selectedQuote)} disabled={converting} className="text-xs bg-blue-600 hover:bg-blue-700 col-span-2">
                    {converting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ShoppingCart className="w-3.5 h-3.5 mr-1.5" />}
                    Créer une commande
                  </Button>
                  <Button size="sm" onClick={() => handleConvertRepair(selectedQuote)} disabled={converting} className="text-xs bg-emerald-600 hover:bg-emerald-700">
                    {converting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5 mr-1.5" />}
                    → Réparation
                  </Button>
                  <Button size="sm" onClick={() => handleConvertInvoice(selectedQuote)} disabled={converting} className="text-xs bg-purple-600 hover:bg-purple-700">
                    {converting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Receipt className="w-3.5 h-3.5 mr-1.5" />}
                    → Facture
                  </Button>
                </div>

                <Button variant="outline" size="sm" onClick={() => handleDelete(selectedQuote)} className="w-full text-xs border-red-500/40 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Supprimer
                </Button>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Dialog Signature */}
      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5 text-primary" /> Signature client
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Le client complète ses informations et signe pour accepter le devis :</p>
            {/* Champs client si pas encore renseignés */}
            <div className="space-y-2">
              <Input
                value={selectedQuote?.client_name || ""}
                onChange={e => setSelectedQuote(q => ({...q, client_name: e.target.value}))}
                placeholder="Nom complet du client *"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={selectedQuote?.client_email || ""}
                  onChange={e => setSelectedQuote(q => ({...q, client_email: e.target.value}))}
                  placeholder="Email"
                  type="email"
                />
                <Input
                  value={selectedQuote?.client_phone || ""}
                  onChange={e => setSelectedQuote(q => ({...q, client_phone: e.target.value}))}
                  placeholder="Téléphone"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground font-medium">Signature (bon pour accord) :</p>
            <div className="border-2 border-border rounded-xl overflow-hidden bg-white">
              <canvas
                ref={canvasRef}
                width={340} height={150}
                className="w-full touch-none cursor-crosshair"
                style={{ display: "block" }}
                onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={clearSig} className="text-xs">
                <X className="w-3.5 h-3.5 mr-1" /> Effacer
              </Button>
              <Button size="sm" onClick={saveSig} className="flex-1 bg-primary text-xs">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Valider la signature
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
